import { TransactionListItem } from "@common/schemas/transaction.schema";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import {
    addTransactionFieldReviews,
    revokeTransactionFieldReview,
} from "@api/transaction.api";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { isViewOnlyRefusalError } from "@lib/viewOnlyRefusal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { CheckIcon, RevertIcon } from "@icons";
import { useUser } from "@hooks/useUser";
import classNames from "classnames";
import { useCallback, useMemo, useState } from "react";
import { updateTransactionInCache } from "../useTransactionsQuery";
import {
    isTransactionFieldReviewedByUser,
    mergeTransactionFieldReviews,
    removeTransactionFieldReview,
} from "./transactionFieldReviewUtils";

interface TransactionFieldReviewButtonProps {
    transaction: Pick<TransactionListItem, "id" | "fieldReviews">;
    field: TransactionReviewField;
    reviewTooltip: string;
    undoTooltip?: string;
    size?: "md" | "lg";
    className?: string;
    /**
     * When true and the field isn't already reviewed by the current user,
     * the button is hidden entirely — blocks marking an ineligible value
     * (e.g. an uncategorized category, or a blank/uncategorized GL account)
     * as reviewed. An already-reviewed field can still be undone even while
     * this is true, so a stale review on a field that later became
     * ineligible (e.g. category changed back to Uncategorized) can be
     * cleared.
     */
    disableMarking?: boolean;
    isReadonly?: boolean;
}

export const TransactionFieldReviewButton = ({
    transaction,
    field,
    reviewTooltip,
    undoTooltip = "Undo review",
    size = "lg",
    className,
    disableMarking,
    isReadonly = false,
}: TransactionFieldReviewButtonProps) => {
    const user = useUser();
    const { error: errorToast } = useToaster();
    const [isReviewOperationInFlight, setIsReviewOperationInFlight] =
        useState(false);
    const isReviewed = useMemo(
        () =>
            isTransactionFieldReviewedByUser(
                transaction.fieldReviews,
                field,
                user?.id,
            ),
        [transaction.fieldReviews, field, user?.id],
    );
    const handleClick = useCallback(async () => {
        if (isReviewOperationInFlight) {
            return;
        }

        setIsReviewOperationInFlight(true);
        try {
            if (isReviewed) {
                await revokeTransactionFieldReview(transaction.id, field);
                updateTransactionInCache(transaction.id, (t) => ({
                    ...t,
                    fieldReviews: removeTransactionFieldReview(
                        t.fieldReviews,
                        field,
                        user?.id,
                    ),
                }));
                return;
            }

            const reviews = await addTransactionFieldReviews(transaction.id, [
                field,
            ]);
            updateTransactionInCache(transaction.id, (t) => ({
                ...t,
                fieldReviews: mergeTransactionFieldReviews(
                    t.fieldReviews,
                    reviews,
                ),
            }));
        } catch (error) {
            if (isViewOnlyRefusalError(error)) {
                return;
            }
            errorToast(
                isReviewed
                    ? "Failed to undo field review. Please try again."
                    : "Failed to mark field as reviewed. Please try again.",
            );
        } finally {
            setIsReviewOperationInFlight(false);
        }
    }, [
        isReviewOperationInFlight,
        isReviewed,
        transaction.id,
        field,
        user?.id,
        errorToast,
    ]);

    if (disableMarking && !isReviewed) {
        return null;
    }

    return (
        <Tooltip
            key={isReviewed ? "reviewed" : "unreviewed"}
            content={isReviewed ? undoTooltip : reviewTooltip}
        >
            <Button
                variant={isReviewed ? "primary" : "accent"}
                size={size}
                className={classNames(
                    viewTableStyles.showOnCellHover,
                    className,
                )}
                aria-label={isReviewed ? undoTooltip : reviewTooltip}
                onClick={handleClick}
                disabled={isReadonly || isReviewOperationInFlight}
                iconOnly
            >
                {isReviewed ? <RevertIcon /> : <CheckIcon />}
            </Button>
        </Tooltip>
    );
};
